import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography } from "@material-ui/core";
import { ToggleButtonGroup, ToggleButton } from "@material-ui/lab";

import { Bundle } from "./AdSizeOptionForms/Bundle";
import { Bespoke } from "./AdSizeOptionForms/Bespoke";
import { AdSizeType, BriefingFormModel } from "pages/BriefingForm/interfaces";
import { Controller } from "react-hook-form";

const isMaxSizesSelected = (selectedIds: string[], id: string, maxLength: number) =>
    selectedIds.length === maxLength && !selectedIds.includes(id);

interface AdSizeSectionProps extends FieldsHookForm<BriefingFormModel> {
    selectedOption: AdSizeType;
    onAdOptionChange: (option: AdSizeType) => void;
    selectedIds: string[];
    // eslint-disable-next-line
    onAdSizeSelect: (onChange: (...event: any[]) => void, clickedId: string | string[]) => void;
    onResetOtherDescriptionValue: () => void;
}

export const AdSizeSection: React.FC<AdSizeSectionProps> = ({
    selectedOption,
    onAdOptionChange,
    control,
    errors,
    selectedIds,
    onAdSizeSelect,
    onResetOtherDescriptionValue,
}) => {
    const adOptionChangeHandler = (selectedOption: AdSizeType) => onAdOptionChange(selectedOption);
    const { t } = useTranslation();

    // eslint-disable-next-line
    const onOptionToggleChange = (onChange: (...event: any[]) => void, option: any) => {
        // Only allow switch, not deselect of both options
        // if deselected, return currently selected value
        if (option) {
            adOptionChangeHandler(option);

            return onChange(option);
        }

        onChange(option);
    };

    return (
        <Box>
            <Container maxWidth="md">
                <Box mb={2}>
                    <Typography variant="subtitle1">{t("briefingForm.info.adSizeInfo")}</Typography>
                </Box>

                <Box mb={2}>
                    <Typography variant="subtitle2">
                        <strong>{t("briefingForm.info.note")}</strong>
                        {t("briefingForm.info.adSizeNote")}
                    </Typography>
                </Box>
            </Container>
            <Container maxWidth="sm">
                <Box display="flex" justifyContent="center">
                    <Controller
                        name="adSize.type"
                        control={control}
                        render={({ onChange, value }) => {
                            return (
                                <ToggleButtonGroup
                                    exclusive
                                    aria-label="text alignment"
                                    value={value}
                                    onChange={(event, value) => onOptionToggleChange(onChange, value)}>
                                    <ToggleButton value="BUNDLE" aria-label="left aligned">
                                        {t("briefingForm.label.bundle")}
                                    </ToggleButton>
                                    <ToggleButton value="BESPOKE" aria-label="centered">
                                        {t("briefingForm.label.bespoke")}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            );
                        }}
                    />
                </Box>

                {selectedOption === "BUNDLE" && (
                    <Bundle
                        control={control}
                        errors={errors}
                        selectedIds={selectedIds}
                        onAdSizeSelect={onAdSizeSelect}
                        isMaxSelected={isMaxSizesSelected}
                    />
                )}

                {selectedOption === "BESPOKE" && (
                    <Bespoke
                        control={control}
                        errors={errors}
                        selectedIds={selectedIds}
                        onAdSizeSelect={onAdSizeSelect}
                        onResetOtherDescriptionValue={onResetOtherDescriptionValue}
                        isMaxSelected={isMaxSizesSelected}
                    />
                )}

                {errors.adSize?.sizes && (
                    <Typography variant="body1" color="error">
                        {/* as any because TS thinks sizes is an array  */}
                        {/* eslint-disable-next-line */}
                        {(errors as any).adSize?.sizes?.message}
                    </Typography>
                )}
            </Container>
        </Box>
    );
};
